import { BR_FEATURE_CONFIG } from '../../../constants/businessRegistration';
import { formatBrLocalIsoDate, parseBrLocalDate } from '../../../constants/brPure';

const CONFIDENCE_THRESHOLD = 0.8;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const EXTENSION_MIME_MAP = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  bmp: 'image/bmp',
  heic: 'image/heic',
  heif: 'image/heif',
  webp: 'image/webp',
  pdf: 'application/pdf',
};

/** 对齐 BusinessLicenseSystemPrompt.txt 输出字段（确认页主要用公司名 / 注册号 / 有效期起迄） */
const F = {
  inputValid: 'input_valid',
  isCompanyRegistrationDocument: 'is_company_registration_document',
  isBusinessLicense: 'isBusinessLicense',
  confidence: 'confidence',
  registrationNumber: 'registration_number',
  companyName: 'company_name',
  effectiveDate: 'effective_date',
  expiryDate: 'expiry_date',
  businessPeriodOrExpiryDate: 'business_period_or_expiry_date',
  documentStatus: 'document_status',
  documentType: 'document_type',
  officialSealText: 'official_seal_text',
  businessLicenseImage: 'BusinessLicenseImage',
};

const OCR_MARKER_KEYS = [
  F.inputValid,
  F.isCompanyRegistrationDocument,
  F.isBusinessLicense,
  F.companyName,
  F.registrationNumber,
];

const PERMANENT_PERIOD_RE = /permanent|no\s*expir|unlimited|lifetime|長期有效|长期有效|永久有效|^永久$|^长期$|^長期$|不设期限|不設期限|無期限|无期限/i;
const SAMPLE_DOCUMENT_RE = /sample|specimen|示例|樣本|样本|標本|标本|for\s*reference\s*only|僅供參考|仅供参考/i;
const OCR_EMPTY_TEXT_RE = /^(?:n\/?a|n\.?\s*a\.?|none|null|undefined|unknown|not\s+found|not\s+applicable|not\s+available|not\s+(?:clearly\s+)?visible|not\s+readable|unreadable|illegible|无法识别|無法識別)$/i;

/** 是否 PDF（file / 文件名 / mime / url） */
export function isBrPdfFile(fileOrName) {
  if (!fileOrName) {
    return false;
  }
  if (typeof fileOrName === 'string') {
    const text = fileOrName.trim().toLowerCase();
    return text === 'application/pdf' || /\.pdf($|\?)/i.test(text);
  }
  const mime = String(fileOrName.type || '').toLowerCase();
  if (mime === 'application/pdf' || mime.includes('pdf')) {
    return true;
  }
  return /\.pdf$/i.test(String(fileOrName.name || ''));
}

export function isAllowedBrFile(file) {
  const allowed = BR_FEATURE_CONFIG.allowedFileTypes || [];
  if (!allowed.length) {
    return true;
  }
  if (allowed.indexOf(file.type) !== -1) {
    return true;
  }
  const extension = (file.name || '').split('.').pop()?.toLowerCase();
  const mappedType = extension ? EXTENSION_MIME_MAP[extension] : '';
  return mappedType ? allowed.indexOf(mappedType) !== -1 : false;
}

/** 解包 OCR 資料（兼容直接 Data、或整包 API 回應） */
export function unwrapOcrPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return {};
  }
  if (hasOcrFields(payload)) {
    return payload;
  }
  if (payload.Data && typeof payload.Data === 'object' && hasOcrFields(payload.Data)) {
    return payload.Data;
  }
  if (payload.data && typeof payload.data === 'object' && hasOcrFields(payload.data)) {
    return payload.data;
  }
  return payload;
}

/**
 * OCR → 确认页「是否有到期日」与起迄日。
 * 规则：只认 effective_date / expiry_date（不认 issue_date）。
 * - 两个都空 → HasExpiry=NO；开始日=今天（供存库）；到期日空
 * - 任一有值 → HasExpiry=YES；有则回填，缺的一侧留空（不默认今天）
 * 确认页切换 Yes/No 时不得再覆盖这些值（见 BrConfirmSubmit.onFormInput）。
 */
export function resolveBrConfirmDatesFromOcr(normalized = {}) {
  const today = formatBrLocalIsoDate(new Date());
  const expiryRaw = String(normalized.expiryDate || '').trim();
  const isPermanentExpiry = isPermanentPeriodText(expiryRaw);
  const startFromOcr = ISO_DATE_RE.test(String(normalized.effectiveDate || '').trim())
    ? String(normalized.effectiveDate).trim()
    : '';
  // 永久措辞视为「无到期日」一侧为空，不把永久原文塞进日期框
  const endFromOcr = isPermanentExpiry || !ISO_DATE_RE.test(expiryRaw) ? '' : expiryRaw;

  if (!startFromOcr && !endFromOcr) {
    return {
      hasExpirationDate: false,
      startDate: today,
      expirationDate: '',
    };
  }

  return {
    hasExpirationDate: true,
    startDate: startFromOcr || '',
    expirationDate: endFromOcr || '',
  };
}

/** Normalize OCR payload（Confirm / Save / 评估所需字段） */
export function normalizeBrOcrPayload(payload = {}) {
  const source = unwrapOcrPayload(payload);
  const keyIndex = buildKeyIndex(source);
  const inputValidRaw = pickOcrField(source, keyIndex, [F.inputValid]);
  const inputValid = (inputValidRaw === undefined || inputValidRaw === null || inputValidRaw === '')
    ? true
    : parseStrictBool(inputValidRaw);

  return {
    inputValid,
    isCompanyRegistrationDocument: parseStrictBool(pickOcrField(source, keyIndex, [
      F.isCompanyRegistrationDocument,
      F.isBusinessLicense,
    ])),
    confidence: parseConfidence(pickOcrField(source, keyIndex, [F.confidence])),
    companyName: preferChineseCompanyName(pickOcrString(source, keyIndex, [F.companyName])),
    registrationNo: pickOcrString(source, keyIndex, [F.registrationNumber]),
    // 只取 OCR 的有效期起迄；空则确认页 HasExpiry=NO
    effectiveDate: sanitizeCalendarDate(pickOcrString(source, keyIndex, [F.effectiveDate])),
    expiryDate: sanitizeCalendarDate(
      pickOcrString(source, keyIndex, [F.expiryDate, F.businessPeriodOrExpiryDate]),
      { allowPermanent: true },
    ),
    documentStatus: pickOcrString(source, keyIndex, [F.documentStatus]),
    documentType: pickOcrString(source, keyIndex, [F.documentType]),
    officialSealText: pickOcrString(source, keyIndex, [F.officialSealText]),
    businessLicenseImage: pickOcrString(source, keyIndex, [F.businessLicenseImage]),
  };
}

/** 提交存庫：有效起迄日只认确认页用户输入；HasExpiry=NO → Expiry 存 null */
export function resolveBrSaveExpiryDate(formData = {}) {
  if (formData.hasExpirationDate !== true) {
    return null;
  }
  const end = String(formData.expirationDate || '').trim();
  return end || null;
}

/** 提交存庫：开始日；HasExpiry=NO 时仍写今天（或表单 startDate） */
export function resolveBrSaveIssueDate(formData = {}) {
  const start = String(formData.startDate || '').trim();
  if (formData.hasExpirationDate !== true) {
    return start || formatBrLocalIsoDate(new Date());
  }
  return start || null;
}

/** 扫描是否可进入确认页 */
export function evaluateBrScanResult(payload, options = {}) {
  const { scanFailed = false, httpStatus = null, alreadyNormalized = false } = options;
  const blockerKeys = [];

  if (scanFailed) {
    blockerKeys.push(httpStatus === 404 ? 'endpoint_not_found' : 'scan_failed');
    return { alertKeys: blockerKeys, blockerKeys, canProceed: false };
  }

  const normalized = alreadyNormalized
    ? (payload || {})
    : normalizeBrOcrPayload(payload);

  if (normalized.inputValid === false) {
    blockerKeys.push('not_license');
    return { alertKeys: blockerKeys, blockerKeys, canProceed: false };
  }

  const hasExtractedData = Boolean(normalized.companyName || normalized.registrationNo);
  const acceptedAsLicense = isAcceptedRegistrationDocument(normalized);

  if (!acceptedAsLicense) {
    blockerKeys.push('not_license');
  } else if (!hasExtractedData) {
    blockerKeys.push('extract_failed');
  }

  return {
    alertKeys: blockerKeys,
    blockerKeys,
    canProceed: blockerKeys.length === 0 && acceptedAsLicense && hasExtractedData,
  };
}

// --- internal helpers ---

function buildKeyIndex(payload) {
  const index = {};
  if (!payload || typeof payload !== 'object') {
    return index;
  }
  Object.keys(payload).forEach((key) => {
    index[key.toLowerCase()] = key;
  });
  return index;
}

function pickOcrField(payload, keyIndex, exactKeys) {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  for (let i = 0; i < exactKeys.length; i += 1) {
    const exactKey = exactKeys[i];
    if (Object.prototype.hasOwnProperty.call(payload, exactKey)) {
      return payload[exactKey];
    }
    const matchedKey = keyIndex[exactKey.toLowerCase()];
    if (matchedKey !== undefined && Object.prototype.hasOwnProperty.call(payload, matchedKey)) {
      return payload[matchedKey];
    }
  }
  return undefined;
}

function pickOcrString(payload, keyIndex, exactKeys) {
  const value = pickOcrField(payload, keyIndex, exactKeys);
  if (value === undefined || value === null) {
    return '';
  }
  return sanitizeEmptyText(String(value).trim());
}

function sanitizeEmptyText(text) {
  const value = String(text || '').trim();
  if (!value || OCR_EMPTY_TEXT_RE.test(value)) {
    return '';
  }
  return value;
}

function parseStrictBool(value) {
  if (value === true || value === 1) {
    return true;
  }
  if (value === false || value === 0) {
    return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
      return true;
    }
    if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === '') {
      return false;
    }
  }
  return false;
}

function parseConfidence(value) {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const num = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(num) || num < 0) {
    return 0;
  }
  return num > 1 ? 1 : num;
}

function hasOcrFields(obj) {
  if (!obj || typeof obj !== 'object') {
    return false;
  }
  const keyIndex = buildKeyIndex(obj);
  return OCR_MARKER_KEYS.some((key) => (
    Object.prototype.hasOwnProperty.call(obj, key)
    || keyIndex[key.toLowerCase()] !== undefined
  ));
}

function isPermanentPeriodText(value) {
  const text = String(value || '').trim();
  return Boolean(text && PERMANENT_PERIOD_RE.test(text));
}

function isSampleDocumentText(...texts) {
  return texts.some((text) => {
    const value = String(text || '').trim();
    return value && SAMPLE_DOCUMENT_RE.test(value);
  });
}

/** Prompt §Tasks：有中文名则只保留中文 */
function preferChineseCompanyName(name) {
  const text = String(name || '').trim();
  if (!text || !/[\u4e00-\u9fff]/.test(text)) {
    return text;
  }

  const segments = text.split(/[/|｜\n\r]+/);
  for (let i = 0; i < segments.length; i += 1) {
    const part = String(segments[i] || '').trim();
    if (!part || !/[\u4e00-\u9fff]/.test(part) || /^[A-Za-z0-9\s.,&'()-]+$/.test(part)) {
      continue;
    }
    return collapseWhitespace(stripLeadingEnglish(stripTrailingEnglish(part)));
  }

  const stripped = collapseWhitespace(stripLeadingEnglish(stripTrailingEnglish(text)));
  if (/[\u4e00-\u9fff]/.test(stripped)) {
    return stripped;
  }

  const cjkRuns = text.match(/[\u4e00-\u9fff\u3400-\u4dbf（）()·、，。]+/g);
  return cjkRuns?.length ? collapseWhitespace(cjkRuns.join('')) : text;
}

function stripTrailingEnglish(text) {
  return String(text || '').replace(/\s*[(/|｜]\s*[A-Za-z][A-Za-z0-9\s.,&'()-]*$/, '').trim();
}

function stripLeadingEnglish(text) {
  return String(text || '').replace(/^[A-Za-z][A-Za-z0-9\s.,&'()-]*\s*[(/|｜]\s*/, '').trim();
}

function collapseWhitespace(text) {
  return String(text || '').trim().replace(/\s+/g, ' ');
}

function sanitizeCalendarDate(raw, { allowPermanent = false } = {}) {
  const text = sanitizeEmptyText(String(raw || '').trim());
  if (!text) {
    return '';
  }
  if (allowPermanent && isPermanentPeriodText(text)) {
    return text;
  }
  if (ISO_DATE_RE.test(text)) {
    return text;
  }
  const parsed = parseBrLocalDate(text);
  return parsed ? formatBrLocalIsoDate(parsed) : '';
}

function isAcceptedRegistrationDocument(normalized = {}) {
  if (normalized.inputValid === false) {
    return false;
  }
  if (isSampleDocumentText(
    normalized.companyName,
    normalized.documentType,
    normalized.documentStatus,
    normalized.officialSealText,
    normalized.registrationNo,
  )) {
    return false;
  }
  if (normalized.isCompanyRegistrationDocument === true) {
    return true;
  }
  const confidence = parseConfidence(normalized.confidence);
  const hasExtractedData = Boolean(normalized.companyName || normalized.registrationNo);
  return confidence >= CONFIDENCE_THRESHOLD && hasExtractedData;
}
